import { useManualPreset } from "@vericus/cadmus-manual";
import { colors, Icon, Spacer, Text } from "@vericus/cadmus-ui";

import { TaskFormat } from "@/generated/graphql";
import { useFeatureFlagsFragment } from "@/graphql/institution-selectors";

import * as Req from "../requirements-rows";
import { SectionProps } from "./shared";

/**
 * Render the public section of assignment requirements
 */
export function VisibleAssignmentRequirements({
  reqs,
  diff,
  setFinalDueDate,
  format,
}: SectionProps) {
  const openManualPreset = useManualPreset();

  return (
    <div data-testid="AssignmentRequirementsContainer">
      <Req.DueDate
        dueDate={reqs.dueDate}
        timeLimit={reqs.noTimeLimit ? null : reqs.timeLimit}
        hasChanged={diff?.dueDate === true || diff?.timeLimit}
        setDate={setFinalDueDate}
      />
      {reqs.draftDueDate !== null &&
        reqs.draftDueDate !== undefined &&
        !reqs.noDraft && (
          <Req.DraftDueDate
            draftDueDate={reqs.draftDueDate}
            hasChanged={diff?.draftDueDate}
          />
        )}
      {reqs.weight !== null && !reqs.noWeight && (
        <Req.Weight weight={reqs.weight} hasChanged={diff?.weight} />
      )}
      {reqs.wordLimit !== null &&
        !reqs.noLimit &&
        format === TaskFormat.Classic && (
          <Req.WordLimit
            isExam={false}
            wordLimit={reqs.wordLimit}
            countReferences={reqs.countReferences}
            countFootnotes={reqs.countFootnotes}
            hasChanged={diff?.wordLimit}
          />
        )}
      {reqs.referencingStyle !== null &&
        !reqs.noReferencing &&
        format === TaskFormat.Classic && (
          <Req.ReferencingStyle
            referencingStyle={reqs.referencingStyle}
            hasChanged={diff?.referencingStyle}
          />
        )}
      {reqs.allowGenerativeAi !== null && (
        <Req.GenerativeAi
          allowGenerativeAi={reqs.allowGenerativeAi}
          hasChanged={diff?.allowGenerativeAi}
          onOpenManualPreset={openManualPreset}
        />
      )}
      <Req.AcademicIntegrity onOpenManualPreset={openManualPreset} />
    </div>
  );
}

/**
 * Render the private section of assignment requirements
 */
export function HiddenAssignmentRequirements({
  reqs,
  diff,
  format,
}: SectionProps) {
  const features = useFeatureFlagsFragment();
  return (
    <>
      <Spacer spacing={27} />
      <Text kind="headingSix" style={{ color: colors.grey600 }} as="div">
        <Icon iconName="Show" iconColor="grey500" /> Additional settings not
        visible to students
      </Text>
      <Spacer spacing={18} />

      {reqs.returnDate != null && (
        <Req.ReturnDate
          returnDate={reqs.returnDate}
          hasChanged={diff?.returnDate}
        />
      )}
      <Req.GradingService
        gradingService={reqs.gradingService}
        hasChanged={diff?.gradingService}
      />
      <Req.AnonymousMarking
        anonymousMarking={reqs.anonymousMarking}
        hasChanged={diff?.anonymousMarking}
      />
      {reqs.maxGrade !== null &&
        (reqs.gradingService === "turnitin" ||
          reqs.gradingService === "cadmusgrader") && (
          <Req.MaxGrade maxGrade={reqs.maxGrade} hasChanged={diff?.maxGrade} />
        )}
      {reqs.gradingService === "cadmusgrader" && (
        <Req.RubricsDisplay
          rubricsDisplay={reqs.rubricDisplayDisabled == false}
          hasChanged={diff?.rubricDisplayDisabled}
        />
      )}
      {features?.turnitin === true && (
        <Req.Similarity
          sViewReports={reqs.sViewReports}
          draftSViewReports={reqs.draftSViewReports}
          noDraft={reqs.noDraft}
          hasChanged={diff?.similarity === true || diff?.draftSimilarity}
        />
      )}
      {reqs.timeLimit !== null && !reqs.noTimeLimit && (
        <Req.TimeLimit
          timeLimit={reqs.timeLimit}
          hasChanged={diff?.timeLimit}
        />
      )}
      {reqs.allowLateResubmission && (
        <Req.AllowLateResubmission hasChanged={diff?.allowLateResubmission} />
      )}
      <Req.SpellCheck
        disableSpellCheck={reqs.disableSpellCheck}
        hasChanged={diff?.disableSpellCheck}
      />
      {format === TaskFormat.Classic && (
        <Req.Copyleaks
          copyleaksEnabled={reqs.copyleaksEnabled}
          hasChanged={diff?.copyleaks}
        />
      )}
    </>
  );
}
